import type { ISbStoryData } from '@storyblok/js'

import { getResolvedNativeApplyContext } from '@percent/cause-dashboard/constants/nativeApply/getResolvedNativeApplyContext'
import { resolveNativeApplyPartnerKey } from '@percent/cause-dashboard/constants/nativeApply/resolveNativeApplyPartner'
import { SavingsPartner } from '@percent/cause-dashboard/common/components/savingsCalculator/savingsCalculator.config'
import type {
  ProductOffer,
  SavingsCalculatorConfig,
  SavingsCalculatorItem
} from '@percent/cause-dashboard/storyblok/types/storyblok-components'

function parsePrice(value: string | undefined): number {
  if (!value) {
    return 0
  }

  const parsed = parseFloat(value.replace(/[^0-9.]/g, ''))

  return Number.isFinite(parsed) ? parsed : 0
}

function resolveProductOffer(item: SavingsCalculatorItem): ISbStoryData<ProductOffer> | null {
  const relation = item.product_offer?.[0]

  if (!relation || typeof relation !== 'object' || relation.content?.component !== 'product_offer') {
    return null
  }

  return relation as ISbStoryData<ProductOffer>
}

function resolveApply(offer: ISbStoryData<ProductOffer>): Pick<SavingsPartner, 'smartApply' | 'partnerUrl'> {
  const { content } = offer

  if (content.primaryCtaType === 'native_apply') {
    const partnerKey = resolveNativeApplyPartnerKey(content.primaryCtaNativeApplyPartner)
    const modalContext = getResolvedNativeApplyContext(offer)

    if (partnerKey && modalContext) {
      return { smartApply: { partnerKey, modalContext } }
    }
  }

  const partnerUrl = content.primaryCtaLink?.trim()

  return partnerUrl ? { partnerUrl } : {}
}

export function buildSavingsPartnerFromItem(item: SavingsCalculatorItem): SavingsPartner | null {
  const offer = resolveProductOffer(item)

  if (!offer) {
    return null
  }

  const displayName = offer.content.title?.trim() ?? ''
  const logo = offer.content.logo?.filename?.trim() ?? ''

  if (!displayName || !logo) {
    return null
  }

  return {
    id: offer.uuid,
    displayName,
    logo,
    monthlyPrice: parsePrice(item.cost_per_user_per_month),
    discountedMonthlyPrice: parsePrice(item.cost_per_user_per_motnh_with_discount),
    discountText: item.discount_percentage_text?.trim() ?? '',
    tooltipText: item.tooltip_text?.trim() || undefined,
    ...resolveApply(offer)
  }
}

export function buildSavingsPartnersFromStory(
  story: ISbStoryData<SavingsCalculatorConfig> | null | undefined
): SavingsPartner[] {
  const items = story?.content?.savings_calculator_items ?? []

  return items
    .map(buildSavingsPartnerFromItem)
    .filter((partner): partner is SavingsPartner => partner !== null)
}
